import React, { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import SALayout from './SALayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Tag, Plus, Loader2, Trash2, Check, X, Pencil } from 'lucide-react';

export default function SAPromoCodes() {
  const { apiFetch } = useSuperAdmin();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', discount_percent: 50, price_per_user: '', duration_months: 12, max_redemptions: ''
  });

  const fetchCodes = useCallback(async () => {
    try {
      const res = await apiFetch('/promo-codes');
      if (res.ok) setCodes(await res.json());
    } catch {} finally { setLoading(false); }
  }, [apiFetch]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleCreate = async () => {
    if (!form.code.trim() || !form.discount_percent) {
      toast.error('Code and discount are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        code: form.code.trim(),
        discount_percent: parseInt(form.discount_percent),
        duration_months: parseInt(form.duration_months) || 12,
        ...(form.price_per_user ? { price_per_user: parseInt(form.price_per_user) } : {}),
        ...(form.max_redemptions ? { max_redemptions: parseInt(form.max_redemptions) } : {})
      };
      const res = await apiFetch('/promo-codes', { method: 'POST', body: JSON.stringify(body) });
      if (res.ok) {
        toast.success('Promo code created');
        setShowCreate(false);
        setForm({ code: '', discount_percent: 50, price_per_user: '', duration_months: 12, max_redemptions: '' });
        fetchCodes();
      } else {
        const err = await res.json();
        toast.error(err.detail);
      }
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handleToggle = async (promo) => {
    try {
      const res = await apiFetch(`/promo-codes/${promo.id}`, {
        method: 'PUT', body: JSON.stringify({ is_active: !promo.is_active })
      });
      if (res.ok) {
        toast.success(`Promo ${promo.is_active ? 'deactivated' : 'activated'}`);
        fetchCodes();
      }
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete promo code "${promo.code}"?`)) return;
    try {
      const res = await apiFetch(`/promo-codes/${promo.id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchCodes(); }
    } catch { toast.error('Failed'); }
  };

  if (loading) {
    return <SALayout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></SALayout>;
  }

  return (
    <SALayout>
      <div className="space-y-4" data-testid="sa-promo-codes">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-slate-900">Promo Codes</h1>
          <Button onClick={() => setShowCreate(true)} className="h-8 text-xs rounded-xl" data-testid="sa-create-promo-btn">
            <Plus className="w-3 h-3 mr-1" /> Create Code
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {codes.map(promo => (
            <Card key={promo.id} className={`border-slate-200/60 ${!promo.is_active ? 'opacity-60' : ''}`} data-testid={`sa-promo-card-${promo.code}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Tag className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{promo.code}</p>
                      <span className={`text-[10px] font-medium ${promo.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {promo.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleToggle(promo)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" title={promo.is_active ? 'Deactivate' : 'Activate'} data-testid={`sa-toggle-promo-${promo.code}`}>
                      {promo.is_active ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => handleDelete(promo)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500" title="Delete" data-testid={`sa-delete-promo-${promo.code}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-slate-50 p-2 rounded-lg">
                    <p className="text-slate-400 text-[10px]">Discount</p>
                    <p className="font-semibold text-slate-700">{promo.discount_percent}%</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-lg">
                    <p className="text-slate-400 text-[10px]">Duration</p>
                    <p className="font-semibold text-slate-700">{promo.duration_months}mo</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-lg">
                    <p className="text-slate-400 text-[10px]">Price/User</p>
                    <p className="font-semibold text-slate-700">{promo.price_per_user ? `₹${promo.price_per_user}` : 'Auto'}</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-lg">
                    <p className="text-slate-400 text-[10px]">Redeemed</p>
                    <p className="font-semibold text-slate-700">{promo.current_redemptions}{promo.max_redemptions ? `/${promo.max_redemptions}` : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {codes.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400 text-sm">No promo codes yet</div>
          )}
        </div>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-base">Create Promo Code</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label className="text-xs">Code</Label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. LAUNCH50" className="h-8 text-xs rounded-lg uppercase" data-testid="sa-promo-form-code" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Discount %</Label>
                  <Input type="number" min={1} max={100} value={form.discount_percent} onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))} className="h-8 text-xs rounded-lg" data-testid="sa-promo-form-discount" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fixed Price/User (optional)</Label>
                  <Input type="number" min={0} value={form.price_per_user} onChange={e => setForm(f => ({ ...f, price_per_user: e.target.value }))} placeholder="₹" className="h-8 text-xs rounded-lg" data-testid="sa-promo-form-price" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Duration (months)</Label>
                  <Input type="number" min={1} value={form.duration_months} onChange={e => setForm(f => ({ ...f, duration_months: e.target.value }))} className="h-8 text-xs rounded-lg" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Max Redemptions</Label>
                  <Input type="number" min={1} value={form.max_redemptions} onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))} placeholder="Unlimited" className="h-8 text-xs rounded-lg" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} className="h-8 text-xs">Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={saving} className="h-8 text-xs rounded-xl" data-testid="sa-promo-form-submit">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SALayout>
  );
}
